import clsx from 'clsx';
import {MRT_Row} from 'material-react-table';
import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  Button,
  designSystemToken,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import {
  BRANDS_SAFETY_ACTION_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {
  updateCreatorBrandSafetyVettingsStatus,
  updateVettingReportStatus,
} from '@/utils/cache-updaters/vettingReportStatus';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {
  type CreatorBrandSafetyVetting,
  type CreatorBrandSafetyVettingStatus,
} from '@/types/creatorBrandSafetyVetting';
import VettingStatusChip from '@/components/vetting-status-chip';
import useTrackBrandSafetyCreatorActionEvent from '@/hooks/analytics/use-track-brand-safety-creator-action-event';
import useUpdateVettingStatus from '@/hooks/mutations/brand-safety/use-update-vetting-status';
import {queryKey} from '@/hooks/queries/use-creator-brand-safety-vettings-query';
import {queryKey as vettingReportQueryKey} from '@/hooks/queries/use-vetting-report-query/useVettingReportQuery';
import useBrandId from '@/hooks/use-brand-id';
import useVettingHubStore from '@/stores/vettingHubStore';
import {PLACEHOLDER_ID} from '../constants';
import styles from './StatusCell.module.scss';

const selectableStatuses: CreatorBrandSafetyVettingStatus[] = [
  'ready_for_review',
  'approved',
  'declined',
];

type StatusCellProps = {
  row: MRT_Row<CreatorBrandSafetyVetting>;
};

function StatusCell({row}: StatusCellProps) {
  const {status, statusTooltip} = row.original;
  const updateVettingStatusMutation = useStatusCellMutation(row);
  const selectedTab = useVettingHubStore(
    useShallow((state) => state.activeTab)
  );

  const isPlaceholder = row.original.id === PLACEHOLDER_ID;

  const trackBrandSafetyCreatorActionEvent =
    useTrackBrandSafetyCreatorActionEvent(FLOW_NAMES.BRAND_SAFETY_CREATOR);

  const onChange = (option: {id: string}) => {
    updateVettingStatusMutation.mutateAsync({
      vettingId: row.original.id,
      status: option.id as CreatorBrandSafetyVettingStatus,
    });

    trackBrandSafetyCreatorActionEvent.action({
      brand_safety_screen_name: 'Hub',
      report_id: row.original.reportId,
      creator_id: row.original.profileId,
      action_name: BRANDS_SAFETY_ACTION_NAMES.VETTING_STATUS_CHANGED,
      action_value: option.id,
      tab: selectedTab,
    });
  };

  const disabledButtonLabel = useMemo(() => {
    if (status === 'vetting_unavailable') {
      return translate('Vetting Unavailable');
    }
    if (status === 'awaiting_opt_out' || status === 'invited') {
      return translate('opt-out-hours', {
        hours: Math.floor(row.original.optOutHoursRemaining ?? 0),
      });
    }
    if (row.original.status === 'report_failed') {
      return translate('Report Failed');
    }
    if (row.original.postCount === 0) {
      return translate('No posts found');
    }
    return translate('queued');
  }, [status]);

  const isEnabled =
    selectableStatuses.includes(row.original.status) &&
    row.original.postCount &&
    row.original.postCount > 0;

  return isEnabled ? (
    <VettingStatusChip
      status={status}
      disabled={isPlaceholder}
      onChange={onChange}
      className={styles.button}
    />
  ) : (
    <Tooltip
      title={statusTooltip}
      arrow
      placement="bottom"
      disableHoverListener={!statusTooltip}
      className={styles.tooltip}
    >
      <div
        className={styles.disabledButtonWrapper}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Button
          disabled
          startIcon={
            statusTooltip ? (
              <Icon
                size="small"
                appearance="neutral"
                name="Actions-Info-Line"
                color={designSystemToken('semantic.fg.disabled')}
              />
            ) : null
          }
          appearance="neutral"
          mode="filled"
          size="small"
          className={clsx(styles.button, styles.buttonInvited)}
        >
          {disabledButtonLabel}
        </Button>
      </div>
    </Tooltip>
  );
}

function useStatusCellMutation(row: StatusCellProps['row']) {
  const brandId = useBrandId();

  return useUpdateVettingStatus({
    onMutate: async ({vettingId, status: newStatus}) => {
      const previousData = updateCreatorBrandSafetyVettingsStatus({
        brandId,
        vettingId,
        status: newStatus,
      });
      updateVettingReportStatus({
        creatorId: row.original.profileId,
        reportId: row.original.reportId,
        status: newStatus as CreatorBrandSafetyVettingStatus,
      });

      return previousData;
    },
    onError: (_err, _data, context) => {
      if (context instanceof Map) {
        context.forEach((data, key) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSettled: () => {
      const {reportId, profileId} = row.original;
      queryClient.invalidateQueries({queryKey: [queryKey, brandId]});
      // invalidate the report so it can be re-fetched with the updated status
      queryClient.invalidateQueries({
        queryKey: [vettingReportQueryKey, profileId, reportId],
      });
    },
  });
}

export default StatusCell;
